import {
    AddAttempt,
    IN_FLIGHT_PROVISIONING_STATES,
    ProvisioningState,
} from './identityProviderManagement.types';

export enum RowStatus {
    ACTIVE = 'ACTIVE',
    PROVISIONING = 'PROVISIONING',
    FAILED = 'FAILED',
}

export interface IdentityProviderRow {
    id: string;
    displayName: string;
    type?: string;
    connectionName?: string;
    cloned: boolean;
    status: RowStatus;
    /**
     * Why a FAILED row failed: the server refused it, or we stopped waiting
     * for an answer. The row explains the two differently.
     */
    failureReason?: 'FAILED' | 'TIMED_OUT';
    /** True for a row that stands in for an add attempt not yet in the list. */
    pending?: boolean;
    /** The add attempt a pending row stands in for. */
    attemptKey?: string;
}

interface ListedIdp {
    id: string;
    displayName: string;
    type?: string;
    [key: string]: unknown;
}

export interface DeriveRowsInput {
    idps: ListedIdp[];
    /** Add actions made on this page, newest first. */
    attempts: AddAttempt[];
}

/** The Auth0 connection name, from `brokerData` (SSO API). */
const connectionNameOf = (idp: ListedIdp): string | undefined => {
    const name = (
        idp.brokerData as { identityProviderName?: unknown } | undefined
    )?.identityProviderName;
    return typeof name === 'string' && name.length > 0 ? name : undefined;
};

/** Compares display names the way an admin reads them: " Okta " is "okta". */
export const nameKey = (name: string) => name.trim().toLocaleLowerCase();

/**
 * Whether the provider list already includes the provider this add attempt
 * created.
 *
 * Matched by id when the server reported one, otherwise by name. The name
 * match applies in every state but FAILED:
 * - the list can include a new provider before polling reports it done;
 * - a timed-out add attempt may still have succeeded.
 * A FAILED add attempt created nothing, so a provider with its name is another
 * one.
 */
export const isListed = (
    idps: Pick<ListedIdp, 'id' | 'displayName'>[],
    attempt: AddAttempt,
): boolean =>
    attempt.identityProviderId
        ? idps.some(i => i.id === attempt.identityProviderId)
        : attempt.state !== ProvisioningState.FAILED &&
          idps.some(
              i =>
                  nameKey(i.displayName) === nameKey(attempt.input.displayName),
          );

const ROW_STATUS: Partial<Record<ProvisioningState, RowStatus>> = {
    [ProvisioningState.SUBMITTING]: RowStatus.PROVISIONING,
    [ProvisioningState.SUBMITTED]: RowStatus.PROVISIONING,
    [ProvisioningState.PROVISIONING]: RowStatus.PROVISIONING,
    [ProvisioningState.PROVISIONED]: RowStatus.ACTIVE,
    [ProvisioningState.FAILED]: RowStatus.FAILED,
    [ProvisioningState.TIMED_OUT]: RowStatus.FAILED,
};

/**
 * The row shown for an add attempt until the provider list includes it.
 * A submit the server rejected outright gets no row: nothing was created.
 */
const pendingRow = (
    idps: ListedIdp[],
    attempt: AddAttempt,
): IdentityProviderRow[] => {
    const status = ROW_STATUS[attempt.state];
    if (!status || isListed(idps, attempt)) return [];

    const failed = status === RowStatus.FAILED;
    return [
        {
            id: attempt.identityProviderId ?? `pending:${attempt.key}`,
            displayName: attempt.input.displayName,
            cloned: false,
            status,
            failureReason: failed
                ? (attempt.state as 'FAILED' | 'TIMED_OUT')
                : undefined,
            pending: true,
            attemptKey: attempt.key,
        },
    ];
};

/**
 * The rows the table shows: pending add attempts first, then the listed
 * providers. The listed providers are shown as the server returns them.
 */
export const deriveIdentityProviderRows = ({
    idps,
    attempts,
}: DeriveRowsInput): IdentityProviderRow[] => [
    ...attempts.flatMap(attempt => pendingRow(idps, attempt)),
    ...idps.map(idp => ({
        id: idp.id,
        displayName: idp.displayName,
        type: idp.type,
        connectionName: connectionNameOf(idp),
        cloned: !!(idp.clonedFromIdpId || idp.clonedFromTenantId),
        status: RowStatus.ACTIVE,
    })),
];

/**
 * Display names a new or renamed provider may not use, so that no two
 * providers look the same in the list, the user picker or the audit log.
 *
 * Taken: every row except a FAILED add attempt, which created nothing. A
 * timed-out add attempt keeps its name because it may still have succeeded.
 *
 * `keep` is the current name of the provider being edited. It stays allowed
 * even if an older provider has the same name.
 */
export const takenNames = (
    rows: IdentityProviderRow[],
    keep?: string,
): ReadonlySet<string> => {
    const kept = keep === undefined ? undefined : nameKey(keep);
    return new Set(
        rows
            .filter(r => r.failureReason !== 'FAILED')
            .map(r => nameKey(r.displayName))
            .filter(name => name !== kept),
    );
};

/** True while the add attempt has not finished. */
export const isInFlight = (attempt: AddAttempt) =>
    IN_FLIGHT_PROVISIONING_STATES.has(attempt.state);
